import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { isString } from 'lodash-es';
import {
  catchError,
  EMPTY,
  from,
  map,
  Observable,
  switchMap,
  throwError,
  timeout,
} from 'rxjs';
import assert from 'tiny-invariant';

import { captureMessage } from '@sentry/angular';

import { AlertService } from '@ts/shared/alert/util-core';
import {
  ApiConsumerCallParams,
  ApiConsumerCallParamsWithBody,
  ApiConsumerCallParamsWithoutBody,
  ApiConsumerService,
  HttpOptions,
  JsonBodyValue,
} from '@ts/shared/api/data-access-api-consumer';
import {
  BORMA_DAGO_ERROR_CODE_FORM_INVALID,
  BormaDagoError,
  BormaDagoHttpErrorResponse,
} from '@ts/shared/api/util-borma-dago';
import { I18nService } from '@ts/shared/i18n/util-core';
import { LoggingService, LogLevel } from '@ts/shared/logging/util-core';
import { EnvironmentService } from '@ts/shared/util-environment';

@Injectable({
  providedIn: 'root',
})
export class BormaDagoApiConsumerService implements ApiConsumerService {
  private http = inject(HttpClient);
  private alertService = inject(AlertService);
  private i18nService = inject(I18nService);
  private loggingService = inject(LoggingService);

  bormaDagoApiUrl: string;
  private isStaffApp: boolean;

  constructor() {
    const environmentService = inject(EnvironmentService);
    const env = environmentService.get();

    this.bormaDagoApiUrl = env.bormaDagoApiUrl;
    this.isStaffApp = env.app === 'soladmin';
  }

  private showError$(
    message: string | Promise<string>,
    error: HttpErrorResponse,
    logLevel: LogLevel,
    isSilent: boolean,
  ): Observable<never> {
    this.loggingService.log(
      logLevel,
      `Attempting to open "${error.url}", but received ${error.status}: ${error.message}`,
    );
    if (!isSilent) {
      this.alertService.error$({
        message,
      });
    }
    return EMPTY;
  }

  private showFormatError$(
    errorTranslationKey: string,
    error: HttpErrorResponse,
    logLevel: LogLevel,
    isSilent: boolean,
    errorCode?: string,
  ): Observable<never> {
    // Both staff and customers get the HTTP status surfaced — it makes the
    // toast actionable (refresh? offline? backend?) without being intrusive.
    // Only staff (soladmin) additionally see the backend ``error_code`` since
    // that is engineering-internal and noise for customers.
    const detailsParts: string[] = [`HTTP ${error.status}`];
    if (this.isStaffApp && errorCode) {
      detailsParts.push(`code: ${errorCode}`);
    }
    const details = ` (${detailsParts.join(', ')})`;

    // Report unknown errors to Sentry so we can quantify and triage them. Other
    // translation keys (entityNotFound, pageNotFound) are expected outcomes and
    // would only be noise. captureMessage is a no-op when Sentry is not
    // initialized (e.g. in tests / dev without sentryDsn).
    if (errorTranslationKey === 'unknown') {
      captureMessage(
        `Unknown API error: ${error.status} ${error.url ?? '(no url)'}`,
        {
          level: 'error',
          tags: {
            http_status: String(error.status),
            error_code: errorCode ?? 'none',
          },
          extra: {
            url: error.url,
            message: error.message,
          },
        },
      );
    }

    this.showError$(
      // we don't want to call translation API if isSilent is true, so we
      // implement this "hack".
      isSilent
        ? ''
        : this.i18nService.translate$(
            `sharedApiDataAccessBormaDagoApiConsumer.${errorTranslationKey}`,
            {
              url: error.url ?? '',
              details,
            },
          ),
      error,
      logLevel,
      isSilent,
    );
    return EMPTY;
  }

  private handleErrorFactory({
    errorCodesToHandleManually,
    isSilent,
  }: Pick<
    Required<ApiConsumerCallParams>,
    'errorCodesToHandleManually' | 'isSilent'
  >) {
    return (error: HttpErrorResponse) => {
      if (error.error instanceof Blob) {
        // If error.error is a Blob, attempt to parse it as JSON
        return this.handleBlobError({
          error,
          errorCodesToHandleManually,
          isSilent,
        });
      }

      return this.processError({ error, errorCodesToHandleManually, isSilent });
    };
  }

  private handleBlobError({
    error,
    errorCodesToHandleManually,
    isSilent,
  }: {
    error: HttpErrorResponse;
  } & Pick<
    Required<ApiConsumerCallParams>,
    'errorCodesToHandleManually' | 'isSilent'
  >): Observable<never> {
    const blobError = error.error as Blob;
    return from(blobError.text()).pipe(
      // Convert Blob to JSON
      map((blobContent) => {
        try {
          const parsedErrorBody = JSON.parse(blobContent);
          return new HttpErrorResponse({
            error: parsedErrorBody,
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url ?? undefined,
          });
        } catch {
          // If parsing fails, return the original error
          return error;
        }
      }),
      // Process the error as usual
      switchMap((parsedError) =>
        this.processError({
          error: parsedError,
          errorCodesToHandleManually,
          isSilent,
        }),
      ),
    );
  }

  private processError({
    error,
    errorCodesToHandleManually,
    isSilent,
  }: {
    error: HttpErrorResponse;
  } & Pick<
    Required<ApiConsumerCallParams>,
    'errorCodesToHandleManually' | 'isSilent'
  >): Observable<never> {
    const maybeErrorObject =
      error.error &&
      typeof error.error === 'object' &&
      !Array.isArray(error.error)
        ? (error.error as BormaDagoError)
        : undefined;
    const errorCodeForDisplay = maybeErrorObject?.error_code;

    const logErrorAndShow = (key: string, logLevel: LogLevel = 'error') =>
      this.showFormatError$(
        key,
        error,
        logLevel,
        isSilent,
        errorCodeForDisplay,
      );

    if (typeof error.error === 'string') {
      return error.error.includes('Maaf, halaman yang anda minta tidak ada.')
        ? logErrorAndShow('pageNotFound', 'warn')
        : logErrorAndShow('unknown');
    }

    if (!error.error) {
      return logErrorAndShow('unknown');
    }

    const errorObject = error.error as BormaDagoError;
    const errorCode = errorObject.error_code;
    const errorDetail = errorObject.detail;

    if (errorCode && errorCodesToHandleManually.includes(errorCode)) {
      return throwError(() => error as BormaDagoHttpErrorResponse);
    }

    if (errorDetail) {
      const errorDetailParsed = isString(errorDetail)
        ? errorDetail
        : JSON.stringify(errorDetail);

      return this.showError$(errorDetailParsed, error, 'warn', isSilent);
    }

    if (error.status === 404) {
      return logErrorAndShow('entityNotFound');
    }

    return logErrorAndShow('unknown');
  }

  private getHttpOptionsWithLanguage(
    baseHttpOptions?: HttpOptions,
  ): HttpOptions {
    const existingHeaders = baseHttpOptions?.headers || new HttpHeaders();

    return {
      ...baseHttpOptions,
      headers: existingHeaders.append(
        'Accept-Language',
        this.i18nService.getLanguageActive(),
      ),
    };
  }

  private paramsProcess(
    body: Exclude<ApiConsumerCallParams['body'], undefined>,
  ): Exclude<ApiConsumerCallParams['body'], undefined> | FormData {
    // If body is an array, don't convert to FormData.
    if (Array.isArray(body)) {
      return body;
    }

    const bodyRecord = body as Record<string, JsonBodyValue | File>;
    // If no file in body, don't convert to FormData.
    if (!Object.values(bodyRecord).find((content) => content instanceof File)) {
      return bodyRecord;
    }

    // Otherwise, convert it to FormData
    const formData = new FormData();
    Object.keys(bodyRecord).forEach((key) => {
      const content = bodyRecord[key];
      if (content instanceof File) {
        formData.append(key, content, content.name);
      } else if (content) {
        formData.append(key, content.toString());
      }
    });
    return formData;
  }

  call$<T>({
    method,
    relativeUrl,
    errorCodesToHandleManually = [],
    queryParams = {},
    httpOptions,
    body,
    isForwardFormErrors,
    isSilent = false,
    timeoutMs,
  }: ApiConsumerCallParams): Observable<T> {
    const url = new URL(relativeUrl, this.bormaDagoApiUrl);
    for (const [key, value] of Object.entries(queryParams)) {
      if (Array.isArray(value)) {
        for (const element of value) {
          url.searchParams.append(key, element);
        }
      } else {
        url.searchParams.set(key, value);
      }
    }
    const completeUrl = url.href;

    // Need omit to bypass typescript http client method
    const httpOptionsWithLanguage = this.getHttpOptionsWithLanguage(
      httpOptions,
    ) as Omit<HttpOptions, 'responseType'>;

    this.loggingService.log(
      'trace',
      `BormaDagoApiConsumerService.call$: ${method.toUpperCase()} ${completeUrl} with body: ${JSON.stringify(body)}`,
    );

    let result: Observable<T>;
    switch (method) {
      case 'get':
        result = this.http.get<T>(completeUrl, httpOptionsWithLanguage);
        break;
      case 'delete':
        result = this.http.delete<T>(completeUrl, httpOptionsWithLanguage);
        break;
      case 'post':
        assert(body);
        result = this.http.post<T>(
          completeUrl,
          this.paramsProcess(body),
          httpOptionsWithLanguage,
        );
        break;
      case 'put':
        assert(body);
        result = this.http.put<T>(
          completeUrl,
          this.paramsProcess(body),
          httpOptionsWithLanguage,
        );
        break;
      case 'patch':
        assert(body);
        result = this.http.patch<T>(
          completeUrl,
          this.paramsProcess(body),
          httpOptionsWithLanguage,
        );
        break;
    }

    if (timeoutMs !== undefined) {
      result = result.pipe(
        timeout({
          first: timeoutMs,
          // Switching away unsubscribes, which aborts the request.
          with: () =>
            this.showFormatError$(
              'timeout',
              new HttpErrorResponse({
                url: completeUrl,
                statusText: 'Timeout',
              }),
              'warn',
              isSilent,
            ),
        }),
      );
    }

    result = result.pipe(
      catchError(
        this.handleErrorFactory({
          errorCodesToHandleManually: [
            ...errorCodesToHandleManually,
            ...(isForwardFormErrors
              ? [BORMA_DAGO_ERROR_CODE_FORM_INVALID]
              : []),
          ],
          isSilent,
        }),
      ),
    );

    return result;
  }

  get$<T>(params: ApiConsumerCallParamsWithoutBody): Observable<T> {
    return this.call$<T>({ ...params, method: 'get' });
  }
  delete$<T>(params: ApiConsumerCallParamsWithoutBody): Observable<T> {
    return this.call$<T>({ ...params, method: 'delete' });
  }
  post$<T>(params: ApiConsumerCallParamsWithBody): Observable<T> {
    return this.call$<T>({ ...params, method: 'post' });
  }
  put$<T>(params: ApiConsumerCallParamsWithBody): Observable<T> {
    return this.call$<T>({ ...params, method: 'put' });
  }
  patch$<T>(params: ApiConsumerCallParamsWithBody): Observable<T> {
    return this.call$<T>({ ...params, method: 'patch' });
  }
}
